import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { titles } from '@utils/translations/en';
import { usePvxPartnerDetailsForm } from '../hooks/usePvxPartnerDetailsForm';
import { DashboardSelectorSubmoduleItem } from '../types/dashboardSelector';
import DashboardSelector from './DashboardSelector';
import FormPageLayout from './FormPageLayout';
import { PvxPartnerDetailsSection } from './PvxPartnerDetailsForm';
import { useCreatePvxPartner } from '../hooks/useCreatePvxPartner';
import { useEditPvxPartner } from '../hooks/useEditPvxPartner';
import { usePartnerFilterEntitlements } from '../hooks/usePartnerFilterEntitlements';
import { buildListableDashboardModules } from '../utils/utils';
import { parsePartnerSubmodules } from '../utils/parseInitialUserModuleAccess';
import { AppAccessSidepanel } from './AppAccessSidepanel';
import AccessSection from './AccessSection';
import { useSelector } from 'react-redux';
import { RootState } from '@stores/store';

type AddPvxPartnerProps = {
  onBack: () => void;
  clientCode?: string;
};

export const AddPvxPartner = ({ onBack, clientCode }: AddPvxPartnerProps) => {
  const isEditMode = !!clientCode;

  const userManagementModules = useSelector(
    (state: RootState) => state.ModulesData.userManagementModules,
  );

  const {
    dashboardData,
    initialPartnerDetails,
    isLoading: isLoadingEditData,
    isSaving: isUpdating,
    handleEdit,
  } = useEditPvxPartner({ clientCode, onSuccess: onBack });

  const { isSaving: isCreating, handleCreate } = useCreatePvxPartner({ onSuccess: onBack });

  const {
    partnerDetails,
    touchedFields,
    formErrors,
    handleFieldChange,
    handleFieldBlur,
    validateAll,
  } = usePvxPartnerDetailsForm({
    initialValues: isEditMode ? initialPartnerDetails : undefined,
    isEditMode,
  });

  const [submodules, setSubmodules] = useState<DashboardSelectorSubmoduleItem[]>([]);

  const {
    gameIds,
    gameItems,
    clientCodes,
    clientCodeItems,
    activeSidepanel,
    openSidepanel,
    closeSidepanel,
    handleSidepanelSave,
    isLoadingEntitlements,
  } = usePartnerFilterEntitlements({ clientCode });

  useEffect(() => {
    if (!userManagementModules?.length) return;
    const catalog = buildListableDashboardModules(userManagementModules);

    if (dashboardData) {
      setSubmodules(parsePartnerSubmodules(dashboardData, catalog));
      return;
    }

    if (isEditMode) return;

    const manageUsersSub = catalog
      .flatMap((m) => m.submodules)
      .find((s) => s.submoduleType === 'CLIENT_USER_MANAGEMENT');
    if (!manageUsersSub) return;

    setSubmodules((prev) =>
      prev.some((s) => s.submoduleType === 'CLIENT_USER_MANAGEMENT')
        ? prev
        : [...prev, { ...manageUsersSub, editable: false }],
    );
  }, [dashboardData, userManagementModules, isEditMode]);

  const handleSave = () => {
    if (!validateAll()) return;
    const appAccess = { filterName: 'game_id' as const, filterValues: gameIds };
    const companyAccess = { filterName: 'client_code' as const, filterValues: clientCodes };
    return isEditMode
      ? handleEdit({ partnerDetails, submodules, appAccess, companyAccess })
      : handleCreate({ partnerDetails, submodules, appAccess, companyAccess });
  };

  const isSaving = isEditMode ? isUpdating : isCreating;
  const isSaveDisabled = isSaving || submodules.length === 0 || isLoadingEntitlements;
  const isLoadingPage = isEditMode && (isLoadingEditData || isLoadingEntitlements);

  const pageTitle = useMemo(() => {
    if (!isEditMode) return titles.NEW_COMPANY;
    return (
      <>
        <Typography
          fontSize="22px"
          fontWeight="600"
          lineHeight="1.5rem"
          fontFamily="Manrope, sans-serif"
          sx={{ color: '#6C6C6C' }}
        >
          {`${titles.EDIT_COMPANY}${partnerDetails.name ? ':' : ''}`}
        </Typography>
        {partnerDetails.name && (
          <Typography
            fontSize="22px"
            fontWeight="600"
            lineHeight="1.5rem"
            fontFamily="Manrope, sans-serif"
            sx={{ color: '#1B1C17' }}
          >
            {partnerDetails.name}
          </Typography>
        )}
      </>
    );
  }, [isEditMode, partnerDetails.name]);

  if (isLoadingPage) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <FormPageLayout
      title={pageTitle}
      onBack={onBack}
      onSave={handleSave}
      onCancel={onBack}
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled || isSaving}
    >
      <PvxPartnerDetailsSection
        partnerDetails={partnerDetails}
        touchedFields={touchedFields}
        formErrors={formErrors}
        handleFieldChange={handleFieldChange}
        handleFieldBlur={handleFieldBlur}
        isEditMode={isEditMode}
      />

      <Divider sx={{ borderColor: '#E8EAEB', my: '1.5rem' }} />

      <AccessSection
        title="App Access"
        addLabel="Add Apps"
        items={gameItems}
        isEditable
        onEdit={() => openSidepanel('game_id', 'Add Apps')}
      />

      <Divider sx={{ borderColor: '#E8EAEB', my: '1.5rem' }} />

      <AccessSection
        title="Company Access"
        addLabel="Add Companies"
        items={clientCodeItems}
        isEditable
        onEdit={() => openSidepanel('client_code', 'Add Companies')}
      />

      <AppAccessSidepanel
        open={!!activeSidepanel}
        onClose={closeSidepanel}
        onSave={handleSidepanelSave}
        title={activeSidepanel?.title ?? ''}
        selectedFilters={activeSidepanel?.selectedIds ?? []}
        filterMap={activeSidepanel?.filterMap ?? {}}
        isLoading={activeSidepanel?.isLoading ?? false}
        subtitleLabel="Company"
        subtitle={partnerDetails.name}
      />

      <Divider sx={{ borderColor: '#E8EAEB', my: '1.5rem' }} />

      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#162936', mb: '1rem' }}>
        {titles.SELECT_DASHBOARDS}
      </Typography>

      <DashboardSelector
        selectedSubmodules={submodules}
        onSelectedSubmodulesChange={setSubmodules}
        showAppAccess={false}
      />
    </FormPageLayout>
  );
};
